import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Drawer,
  useMediaQuery,
  Chip,
  TextField,
  InputAdornment,
} from '@mui/material';

import {
  Person,
  Settings,
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Bed,
  Close as CloseIcon,
  Search as SearchIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import ScienceIcon from '@mui/icons-material/Science';
import AnimationIcon from '@mui/icons-material/Animation';
import AssistWalkerIcon from '@mui/icons-material/AssistWalker';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import BackspaceIcon from '@mui/icons-material/Backspace';
import Department from './AdminPanel/Department';
import SystemUser from './AdminPanel/SystemUser';
import Role from './AdminPanel/Role';
import Insurance from './AdminPanel/Insurance';
import LowPriorityIcon from '@mui/icons-material/LowPriority';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import SwitchAccessShortcutAddIcon from '@mui/icons-material/SwitchAccessShortcutAdd';
import Access from './AdminPanel/Access';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import ComponentAccess from './AdminPanel/ComponentAccess';
import ExtensionIcon from '@mui/icons-material/Extension';
import WardsBeds from './AdminPanel/WardsBeds';
import ServicesSetting from './AdminPanel/ServicesSetting';
import Diagnosis from './AdminPanel/Diagnosis';
import Procedure from './AdminPanel/Procedure';
import Laboratory from './AdminPanel/Laboratory';
import Scan from './AdminPanel/Scan';
import SmsMessage from './AdminPanel/SmsMessage';
import SmsIcon from '@mui/icons-material/Sms';
import HospitalAdminSettings from './AdminPanel/Hospitaladminsettings';
import { Import } from 'lucide-react';
import ApproveRequestWriteoff from './AdminPanel/ApproveRequestWriteoff';
import PolicySOPManagement from './AdminPanel/PolicySOPManagement';
import NurseWardAssignment from './AdminPanel/NurseWardAssignment';
import AuditLog from './AdminPanel/AuditLog';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import SubBranches from './AdminPanel/SubBranches';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import axios from 'axios';
import { branchesList } from './BaseURL';
import { BRAND, ADMIN, FONT_STACK, GRADIENT_GREEN } from './AdminPanel/adminUi';

// ============================================================================
//  AdminPanel — the shell
// ============================================================================
//
//  Twenty-one configuration screens behind one sidebar. This file owns the
//  chrome only; each screen owns itself.
//
//  What changed, and why:
//
//    · Colour. The sidebar was hard-coded to a teal (#0e926a) that appears
//      nowhere else in the product, so the panel read as a different
//      application from the one it lives inside. It now takes navy + apex
//      green from brand.js, like everything else.
//
//    · Height. The panel sets its own 100vh while sitting INSIDE MainLayout,
//      which already reserves space for a fixed AppBar and adds 24px of
//      padding. The sums did not cancel, so the page was always taller than
//      the viewport and the sidebar grew its own second scrollbar. Both are
//      now measured against the space the panel actually has.
//
//    · Phones. Twenty-one menu entries is a lot to thumb through on a 360px
//      screen with no way to filter, so the sidebar has a search box, and the
//      mobile header carries the current screen's name — previously the only
//      way to know where you were was to reopen the drawer.

// The screen the Admin Panel opens on. The Activity Log is the home of the
// panel: before an administrator changes anything, the useful question is what
// the system has been doing and who has been doing it. Landing on a
// configuration form instead — which is what id 0, System Departments, was —
// puts an edit screen in front of someone who came to look.
const HOME_TAB = 18;

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState(HOME_TAB);
  const [userInfo, setUserInfo] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [filter, setFilter] = useState('');

  // Sub-branches are hidden from the menu when the facility runs only the
  // main branch. System Administrators still see the entry so they can
  // create the first sub-branch when they're ready to.
  const [branchCount, setBranchCount] = useState(1);

  // Queried against a literal rather than theme.breakpoints so the value is
  // readable here: below 900px the sidebar becomes a drawer.
  const isMobile = useMediaQuery('(max-width:899px)');

  useEffect(() => {
    try {
      const raw = localStorage.getItem('userInfo');
      if (raw) setUserInfo(JSON.parse(raw));
    } catch (_) {}
    (async () => {
      try {
        const { data } = await axios.get(branchesList, { timeout: 8000 });
        if (data?.success) setBranchCount((data.results || []).length);
      } catch (_) { /* leave default */ }
    })();
  }, []);

  useEffect(() => {
    if (userInfo && userInfo.is_staff === 0) {
      // Handle non-staff user
    }
  }, [userInfo]);

  const showBranchAdmin = branchCount > 1 || userInfo?.role === 'System Administrator';

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  // Sidebar layout — grouped into the three sections requested:
  //   Insurance     → insurance company config
  //   Administrative → system, users, departments, clinical setup, Ward Assignments
  //   Accounting    → financial control (write-offs, etc.)
  // Each item still has a unique numeric id so activeTab logic is unchanged.
  const menuGroups = [
    // `ownsHeader` marks a screen that has been converted to adminUi and
    // renders its own <AdminPageHead>. The shell then skips its generic
    // heading rather than stacking two titles. Set it as each screen is
    // converted.
    {
      title: 'Overview',
      items: [
        { id: HOME_TAB, label: 'Activity Log',      icon: <HistoryEduIcon />,              component: <AuditLog />,  ownsHeader: true },
      ],
    },
    {
      title: 'Insurance',
      items: [
        { id: 7,  label: 'Insurance Companies',     icon: <AddBusinessIcon />,             component: <Insurance />, ownsHeader: true },
      ],
    },
    {
      title: 'Administrative',
      items: [
        { id: 6,  label: 'System Settings',         icon: <Settings />,                    component: <HospitalAdminSettings /> },
        ...(showBranchAdmin
          ? [{ id: 19, label: 'Sub-Branches',       icon: <AccountTreeIcon />,             component: <SubBranches /> }]
          : []),
        { id: 0,  label: 'System Departments',      icon: <Person />,                      component: <Department /> },
        { id: 1,  label: 'Policy / SOP Management', icon: <AssignmentLateIcon />,          component: <PolicySOPManagement />, ownsHeader: true },
        { id: 2,  label: 'User Roles',              icon: <LowPriorityIcon />,             component: <Role />, ownsHeader: true },
        { id: 3,  label: 'System Users',            icon: <GroupAddIcon />,                component: <SystemUser />, ownsHeader: true },
        // 'Staff Registration' (id 4) removed — superseded by System Users.
        // It made no backend calls at all, so its table was permanently empty;
        // id 4 is left unused rather than reassigned, so nothing that ever
        // stored the old tab number lands on a different screen.
        { id: 5,  label: 'Department Access',       icon: <SwitchAccessShortcutAddIcon />, component: <Access />, ownsHeader: true },
        { id: 8,  label: 'Component Access',        icon: <ExtensionIcon />,               component: <ComponentAccess />, ownsHeader: true },
        { id: 9,  label: 'Wards & Beds',            icon: <Bed />,                         component: <WardsBeds />, ownsHeader: true },
        { id: 17, label: 'Ward Assignments',        icon: <AssignmentIcon />,              component: <NurseWardAssignment /> },
        { id: 10, label: 'Services Setup',          icon: <AssignmentIcon />,              component: <ServicesSetting />, ownsHeader: true },
        { id: 12, label: 'Diagnosis Setup',         icon: <AssistWalkerIcon />,            component: <Diagnosis />, ownsHeader: true },
        { id: 13, label: 'Procedure Setup',         icon: <AssignmentLateIcon />,          component: <Procedure /> },
        { id: 14, label: 'Laboratory Setup',        icon: <ScienceIcon />,                 component: <Laboratory /> },
        { id: 15, label: 'Scan Setup',              icon: <AnimationIcon />,               component: <Scan /> },
        { id: 16, label: 'SMS Messaging',           icon: <SmsIcon />,                     component: <SmsMessage /> },
      ],
    },
    {
      title: 'Accounting',
      items: [
        { id: 11, label: 'Write-off Approvals',     icon: <BackspaceIcon />,               component: <ApproveRequestWriteoff /> },
      ],
    },
  ];

  // Flat list for activeTab → component lookup.
  const menuItems = menuGroups.flatMap(g => g.items);
  const activeItem = menuItems.find(i => i.id === activeTab);

  // Search narrows the menu without changing which screen is open — filtering
  // is a way to FIND something, and having it silently navigate away from the
  // screen you are working on would be hostile.
  const visibleGroups = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return menuGroups;
    return menuGroups
      .map(g => ({ ...g, items: g.items.filter(i => i.label.toLowerCase().includes(q)) }))
      .filter(g => g.items.length > 0);
    // menuGroups is rebuilt every render (it holds JSX), so depending on it
    // would defeat the memo entirely. The inputs that actually matter are the
    // query and the two flags that add or remove entries.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, showBranchAdmin, activeTab]);

  const noMatches = filter.trim() && visibleGroups.length === 0;

  // Sidebar content
  const sidebarContent = (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        // The whole rail is navy now, not just its header. A white sidebar
        // under a navy cap read as two components stacked; one continuous
        // surface reads as one piece of chrome, and it separates the
        // navigation from the work area without needing a border to do it.
        background: `linear-gradient(180deg, ${BRAND.navy} 0%, ${BRAND.navyDark} 100%)`,
        color: '#fff',
        fontFamily: FONT_STACK,
        // Dark backgrounds render type visually heavier than light ones at the
        // same weight. Antialiasing it back down is what makes the difference
        // between "bold and blocky" and "smooth" here.
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5, py: 2.25,
          flexShrink: 0,
          borderBottom: '1px solid rgba(255,255,255,.10)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
          <Box
            sx={{
              width: 42, height: 42, borderRadius: '12px', flexShrink: 0,
              background: GRADIENT_GREEN, display: 'grid', placeItems: 'center',
              boxShadow: `0 6px 16px ${BRAND.green}4D`,
            }}
          >
            <DashboardIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 15.5, fontWeight: 700, letterSpacing: '.1px', lineHeight: 1.25 }}>
              Admin Panel
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: 'rgba(255,255,255,.62)', mt: .15, fontWeight: 500 }}>
              System Management
            </Typography>
          </Box>

          {/* Only on mobile: the drawer needs its own way out. Reaching for
              the backdrop is not obvious on a phone, and there is no visible
              page behind it to tap. */}
          {isMobile && (
            <IconButton
              size="small" aria-label="Close menu"
              onClick={() => setMobileOpen(false)}
              sx={{ color: '#fff', background: 'rgba(255,255,255,.12)', '&:hover': { background: 'rgba(255,255,255,.22)' } }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Filter. Twenty-one entries is past the point where scanning works. */}
      <Box sx={{ px: 1.75, pt: 1.75, pb: 1, flexShrink: 0 }}>
        <TextField
          fullWidth size="small" placeholder="Search settings…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 17, color: 'rgba(255,255,255,.45)' }} />
              </InputAdornment>
            ),
            endAdornment: filter ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Clear search" onClick={() => setFilter('')}>
                  <CloseIcon sx={{ fontSize: 15, color: 'rgba(255,255,255,.55)' }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          // Inverted for the dark rail. adminFieldSx assumes a white ground,
          // so this field is styled from scratch rather than patched — a
          // half-inverted input is worse than either.
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: 13,
              borderRadius: '9px',
              color: '#fff',
              background: 'rgba(255,255,255,.07)',
              transition: 'background-color .18s ease',
              '& fieldset':             { borderColor: 'rgba(255,255,255,.14)' },
              '&:hover fieldset':       { borderColor: 'rgba(255,255,255,.28)' },
              '&:hover':                { background: 'rgba(255,255,255,.10)' },
              '&.Mui-focused fieldset': { borderColor: BRAND.greenLight, borderWidth: '1.5px' },
              '&.Mui-focused':          { background: 'rgba(255,255,255,.12)' },
            },
            '& input::placeholder': { color: 'rgba(255,255,255,.45)', opacity: 1 },
          }}
        />
      </Box>

      {/* Menu — grouped sections with subtle headers */}
      <Box sx={{ flexGrow: 1, minHeight: 0, pb: 1, overflowY: 'auto' }}>
        {noMatches && (
          <Typography sx={{ px: 3, py: 3, fontSize: 12.5, color: 'rgba(255,255,255,.55)', textAlign: 'center', lineHeight: 1.7 }}>
            Nothing matches “{filter.trim()}”.
          </Typography>
        )}

        {visibleGroups.map((group, gi) => (
          <Box key={group.title} sx={{ mb: gi === visibleGroups.length - 1 ? 0 : .5 }}>
            {/* Section header. Deliberately quiet — these are signposts, not
                items. On navy that means low opacity plus wide tracking,
                which stays legible where a simple grey would go muddy. */}
            <Typography sx={{
              px: 2.5, pt: gi === 0 ? 1 : 2, pb: 0.5,
              fontSize: 10, fontWeight: 700,
              letterSpacing: '1.3px',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,.42)',
            }}>
              {group.title}
            </Typography>

            <List dense disablePadding sx={{ px: 1.25 }}>
              {group.items.map((item) => {
                const selected = activeTab === item.id;
                return (
                  <ListItemButton
                    key={item.id}
                    selected={selected}
                    onClick={(e) => handleTabChange(e, item.id)}
                    sx={{
                      borderRadius: '9px',
                      mb: 0.25,
                      // 42px on touch: below about 40 the entries become hard
                      // to hit reliably with a thumb.
                      minHeight: { xs: 42, md: 36 },
                      py: 0.5,
                      pl: 1.25,
                      transition: 'background-color .18s ease, color .18s ease',
                      position: 'relative',
                      // Apex green marks the active row. It is the only
                      // saturated colour on the rail, so it is unmissable
                      // without anything else having to shout.
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0, top: '22%', bottom: '22%',
                        width: 3,
                        borderRadius: '0 3px 3px 0',
                        bgcolor: selected ? BRAND.greenLight : 'transparent',
                      },
                      '&:hover': { bgcolor: 'rgba(255,255,255,.07)' },
                      '&.Mui-selected': {
                        bgcolor: 'rgba(255,255,255,.12)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,.15)' },
                      },
                    }}
                  >
                    <ListItemIcon sx={{
                      minWidth: 30,
                      color: selected ? BRAND.greenLight : 'rgba(255,255,255,.52)',
                      transition: 'color .18s ease',
                      '& svg': { fontSize: 18 },
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        // 600 rather than 700 for the active row: on a dark
                        // ground the background change already carries the
                        // state, and heavy weight on navy turns to a smudge.
                        fontWeight: selected ? 600 : 500,
                        fontSize: 13,
                        letterSpacing: '.1px',
                        color: selected ? '#fff' : 'rgba(255,255,255,.80)',
                        // Long labels truncate rather than wrapping to two
                        // lines and making one entry taller than the rest.
                        noWrap: true,
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          px: 2.5, py: 1.5, flexShrink: 0,
          borderTop: '1px solid rgba(255,255,255,.10)',
          background: 'rgba(0,0,0,.14)',
          display: 'flex', alignItems: 'center', gap: 1,
        }}
      >
        <Settings sx={{ fontSize: 16, color: 'rgba(255,255,255,.45)' }} />
        <Typography sx={{ fontSize: 11.5, color: 'rgba(255,255,255,.62)', fontWeight: 500, flex: 1 }}>
          System Settings
        </Typography>
        <Chip
          label="v2.0.1"
          size="small"
          sx={{
            fontSize: 10.5, height: 20, fontWeight: 700,
            bgcolor: 'rgba(63,191,100,.18)', color: BRAND.greenLight,
          }}
        />
      </Box>
    </Box>
  );

  // ── Layout model ──────────────────────────────────────────────────────────
  //
  //  The panel does NOT size itself to the viewport any more. It used to:
  //  `height: calc(100vh - appbar - padding)`, with the content area scrolling
  //  inside it. That is the right shape for an app, but it only works if the
  //  arithmetic is exact, and it cannot be — MainLayout's <ToolbarSpacer> sets
  //  a MIN height, so the real AppBar is as tall as its contents (a 38px logo
  //  plus button padding), which is not a number this file can know.
  //
  //  Being wrong by a few pixels produced exactly what you would expect: the
  //  panel slightly taller than its slot, so the page scrolled AS WELL as the
  //  content area — two scrollbars — and the panel's bottom edge fell below
  //  the fold, cutting the last table row in half and leaving a band of dead
  //  canvas underneath it.
  //
  //  So: the PAGE scrolls, once, like a document. The rail is sticky instead
  //  of fixed-height, and the content area has no scroller of its own. The
  //  only measured number left is where the rail comes to rest, and being a
  //  few pixels out there just means it sits a little high.
  const railHeight = {
    xs: `calc(100vh - ${ADMIN.stickyTop.xs}px - 16px)`,
    sm: `calc(100vh - ${ADMIN.stickyTop.sm}px - 16px)`,
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: { md: 2.5 },
        // Padding on the ROW, so the rail and the content column drop together
        // and stay aligned at the top. Putting it on the content column alone
        // would leave the navy rail sitting a few pixels higher than the header
        // beside it, which is more noticeable than no gap at all.
        pt: ADMIN.topGap,
        pb: 2,
        fontFamily: FONT_STACK,
      }}
    >
      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              // Never wider than the screen. A fixed 280px drawer on a 320px
              // phone leaves 40px of backdrop — not enough to tap to dismiss.
              width: 'min(86vw, 300px)',
              boxShadow: '4px 0 28px rgba(15,31,61,.18)',
            },
          }}
        >
          {sidebarContent}
        </Drawer>
      )}

      {/* Desktop Sidebar — its own card, stuck below the AppBar. It stays put
          while the page scrolls past it, and its list scrolls internally when
          it is taller than the screen. */}
      {!isMobile && (
        <Box
          sx={{
            width: ADMIN.sidebarWidth,
            flexShrink: 0,
            position: 'sticky',
            top: ADMIN.stickyTop,
            height: railHeight,
            borderRadius: ADMIN.radius,
            overflow: 'hidden',
            boxShadow: '0 8px 28px rgba(15,31,61,.14)',
          }}
        >
          {sidebarContent}
        </Box>
      )}

      {/* Main Content — no scroller of its own; it grows and the page scrolls. */}
      <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Mobile Header. Sticky so the way back to the menu is always one tap
            away — on a phone this bar IS the navigation. */}
        {isMobile && (
          <Box
            sx={{
              px: 2, py: 1.5, flexShrink: 0,
              position: 'sticky',
              top: ADMIN.stickyTop.xs,
              zIndex: 3,
              borderRadius: ADMIN.radiusSm,
              mb: 1.75,
              background: `linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.navyDark} 100%)`,
              color: '#fff',
              boxShadow: '0 6px 20px rgba(15,31,61,.20)',
              display: 'flex', alignItems: 'center', gap: 1.5,
            }}
          >
            <IconButton
              aria-label="Open menu"
              onClick={() => setMobileOpen(true)}
              sx={{
                color: '#fff', flexShrink: 0,
                bgcolor: 'rgba(255,255,255,.12)',
                '&:hover': { bgcolor: 'rgba(255,255,255,.22)' },
              }}
            >
              <MenuIcon />
            </IconButton>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.8px', textTransform: 'uppercase', opacity: .72 }}>
                Admin Panel
              </Typography>
              <Typography noWrap sx={{ fontSize: 15, fontWeight: 800, lineHeight: 1.25 }}>
                {activeItem?.label}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Content Area. No scroller — the page scrolls. overflowX stays
            `auto` because nineteen of these screens are not converted yet and
            several carry tables far wider than a phone; scrolling one here
            beats letting it drag the whole layout sideways. */}
        <Box sx={{ minWidth: 0, overflowX: 'auto' }}>
          {/* Page header for desktop.
              Skipped for screens that render their own <AdminPageHead>. Both
              were showing, so "Activity Log" appeared twice — once as this
              plain heading with the generic "Manage and configure your system
              settings" underneath, and again in the screen's own navy band
              with the subtitle that actually says something. */}
          {!isMobile && !activeItem?.ownsHeader && (
            <Box sx={{ mb: ADMIN.headGap, px: .25 }}>
              <Typography sx={{ fontSize: 24, fontWeight: 800, color: BRAND.navy, letterSpacing: '-.4px', lineHeight: 1.2 }}>
                {activeItem?.label}
              </Typography>
              <Typography sx={{ fontSize: 13, color: BRAND.inkMuted, mt: .35 }}>
                Manage and configure your system settings
              </Typography>
            </Box>
          )}

          {/* Dynamic Content. Keyed on the active screen so the transition
              replays on every change — without a key React reuses the node
              and the animation only ever runs once, on first mount. */}
          <Box
            key={activeTab}
            sx={{
              animation: 'adminFadeIn .32s ease',
              '@keyframes adminFadeIn': {
                from: { opacity: 0, transform: 'translateY(8px)' },
                to:   { opacity: 1, transform: 'translateY(0)' },
              },
              // Respects the OS "reduce motion" setting. Vestibular disorders
              // are common enough that a staff-facing app should not assume.
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }}
          >
            {activeItem?.component}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminPanel;